/**
 * Admin Customer Roster (Iter 67ee).
 *
 * Live directory of every customer account with full PII (name, email,
 * phone, address, country) plus a CSV export for offline marketing
 * work. Sits inside the Admin Ops panel under a new "Customers" tab.
 *
 * Pairs with the existing broadcast-notification endpoint: clicking
 * "Email selected" pre-populates the audience field on the
 * Notifications tab with the selected customer IDs.
 *
 * Designed mobile-friendly because admins audit GDPR requests from
 * phones surprisingly often (we kept seeing /admin opened from iPad
 * in the analytics).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, Search, Send, Loader2, Users, Mail, Phone, MapPin, Globe, RefreshCw, Sparkles, MessageSquare, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { impersonate } from "@/components/AdminImpersonationBanner";

export default function AdminCustomerRoster({ onBroadcastSelected }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/roster`, {
        params: { search, limit: 1000 },
      });
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial load + debounce on search.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  // CSV download. We hit the dedicated endpoint instead of building the
  // CSV client-side so the file the admin gets is the same one the
  // server uses for audit + IT export.
  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/customers/roster.csv?search=${encodeURIComponent(search)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1]
        || `rentalcar-customer-roster.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Could not download CSV. Try again or use a different browser.");
    }
  };

  const summary = useMemo(() => ({
    total: rows.length,
    withPhone: rows.filter((r) => r.phone).length,
    withAddress: rows.filter((r) => r.address).length,
    repeat: rows.filter((r) => (r.booking_count || 0) > 1).length,
    // Iter 68f — leads-only vs full customer accounts.
    leads: rows.filter((r) => r.source === "lead").length,
    chatTouched: rows.filter((r) => (r.chat_messages || 0) > 0 || r.source === "lead" || r.source === "both").length,
  }), [rows]);

  return (
    <div className="space-y-4" data-testid="admin-customer-roster">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            Customer directory
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Full PII for every customer account · use for marketing campaigns, CRM imports,
            and GDPR access requests. Phone &amp; address are pulled from the user profile,
            falling back to the most recent booking's driver details when the profile is empty.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-full"
          data-testid="customer-roster-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
        <Button
          type="button"
          onClick={downloadCsv}
          className="rounded-full bg-blue-600 hover:bg-blue-700"
          data-testid="customer-roster-csv"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Download CSV
        </Button>
      </header>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="customer-roster-summary">
        <_Stat label="Total contacts"  value={summary.total} icon={Users}  tone="blue" />
        <_Stat label="Chat-touched"    value={summary.chatTouched} icon={MessageSquare} tone="amber" />
        <_Stat label="Leads only"      value={summary.leads} icon={Sparkles} tone="violet" />
        <_Stat label="With phone"      value={summary.withPhone} icon={Phone} tone="emerald" />
        <_Stat label="With address"    value={summary.withAddress} icon={MapPin} tone="violet" />
        <_Stat label="Repeat renters"  value={summary.repeat} icon={Mail} tone="amber" />
      </div>

      {/* Search + bulk-action bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, address…"
            className="pl-8 rounded-full h-9"
            data-testid="customer-roster-search"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onBroadcastSelected?.([...selected])}
          disabled={selected.size === 0 || !onBroadcastSelected}
          className="rounded-full"
          data-testid="customer-roster-broadcast"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Email {selected.size > 0 ? `${selected.size} selected` : "selected"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                    aria-label="Select all"
                    className="h-3.5 w-3.5"
                    data-testid="customer-roster-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Address</th>
                <th className="px-3 py-2 text-left"><Globe className="h-3 w-3 inline" /></th>
                <th className="px-3 py-2 text-left">Chat</th>
                <th className="px-3 py-2 text-left">Bookings</th>
                <th className="px-3 py-2 text-left">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan="10" className="px-3 py-6 text-center text-neutral-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="10" className="px-3 py-6 text-center text-neutral-500">No customers match this search.</td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-border ${selected.has(r.id) ? "bg-blue-50/50" : "hover:bg-neutral-50/60"}`}
                  data-testid={`customer-roster-row-${r.id}`}
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      aria-label={`Select ${r.full_name}`}
                      className="h-3.5 w-3.5"
                      data-testid={`customer-roster-select-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-neutral-900">
                    {r.full_name || "—"}
                    <button
                      type="button"
                      onClick={() => impersonate(r.id, "customer").catch(() => toast.error("This contact has no customer account yet"))}
                      className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-border text-neutral-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 transition align-middle"
                      title="Open this client's dashboard as admin (audited)"
                      data-testid={`customer-open-dashboard-${r.id}`}
                    >
                      <ExternalLink className="h-2.5 w-2.5" /> Open
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <SourceBadge source={r.source} />
                  </td>
                  <td className="px-3 py-2">
                    <a href={`mailto:${r.email}`} className="text-blue-700 hover:underline">{r.email}</a>
                  </td>
                  <td className="px-3 py-2">
                    {r.phone ? (
                      <a href={`tel:${r.phone}`} className="text-neutral-800 hover:underline">{r.phone}</a>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 max-w-[260px] truncate text-neutral-700" title={r.address || ""}>
                    {r.address || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 uppercase tracking-wider text-neutral-500">{r.country || "—"}</td>
                  <td className="px-3 py-2">
                    {(r.chat_messages || 0) + (r.chat_handoffs || 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800">
                        <MessageSquare className="h-3 w-3" /> {r.chat_messages || 0}
                        {(r.chat_handoffs || 0) > 0 && (
                          <span className="text-emerald-700">· {r.chat_handoffs}↗</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.booking_count > 0
                      ? <Badge variant="secondary" className="text-[10px]">{r.booking_count}</Badge>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-neutral-500 text-[11px]">
                    {r.latest_booking_at
                      ? <>booked <span className="font-medium">{new Date(r.latest_booking_at).toLocaleDateString()}</span></>
                      : r.last_chat_at
                        ? <>chat <span className="font-medium">{new Date(r.last_chat_at).toLocaleDateString()}</span></>
                        : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-neutral-400 leading-relaxed">
        Data shown here includes personal information protected by GDPR. Access is
        restricted to admins and agents with the <code>view_customers</code> permission.
        Downloaded CSV exports include the admin ID + timestamp in the filename for
        audit purposes — handle accordingly.
      </p>
    </div>
  );
}

function _Stat({ label, value, icon: Icon, tone }) {
  const tones = {
    blue:    "bg-blue-50 text-blue-700 ring-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    violet:  "bg-violet-50 text-violet-700 ring-violet-200",
    amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  };
  return (
    <div className={`rounded-xl ring-1 px-3 py-2 ${tones[tone] || tones.blue}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-display font-medium leading-tight mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}


function SourceBadge({ source }) {
  // Iter 68f — provenance pill so the operator can tell at a glance
  // whether a contact came from a real registration, an anonymous chat
  // capture, or both.
  if (source === "lead") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-200">
        <Sparkles className="h-2.5 w-2.5" /> Lead
      </span>
    );
  }
  if (source === "both") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 ring-1 ring-violet-200">
        <Sparkles className="h-2.5 w-2.5" /> User + Lead
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-200">
      <Users className="h-2.5 w-2.5" /> User
    </span>
  );
}
